import React from "react";
import { useNavigate } from "react-router-dom";
import { Download, Printer, Plus, RotateCw } from "lucide-react";
import { toast } from "sonner";
import {
  BackLink,
  HeroStatus,
  Section,
  EntityCard,
  Card,
  CardTitle,
  KvGrid,
  KvItem,
  Divider,
  Prose,
  FooterActions,
  ActionButton,
  TechDetailsToggle,
  fmtItalianDateTime,
  orDash,
  upperOrDash,
  EM_DASH,
} from "./Primitives";

/* ============================================================
 * EU country mapping (VIES code -> Italian country name)
 * ============================================================ */

const EU_COUNTRIES = {
  AT: "Austria", BE: "Belgio", BG: "Bulgaria",
  CY: "Cipro", CZ: "Cechia", DE: "Germania",
  DK: "Danimarca", EE: "Estonia", EL: "Grecia",
  ES: "Spagna", FI: "Finlandia", FR: "Francia",
  HR: "Croazia", HU: "Ungheria", IE: "Irlanda",
  IT: "Italia", LT: "Lituania", LU: "Lussemburgo",
  LV: "Lettonia", MT: "Malta", NL: "Paesi Bassi",
  PL: "Polonia", PT: "Portogallo", RO: "Romania",
  SE: "Svezia", SI: "Slovenia", SK: "Slovacchia",
  XI: "Irlanda del Nord",
};

const countryLabel = (code) => {
  if (!code) return EM_DASH;
  const c = String(code).toUpperCase();
  return EU_COUNTRIES[c] ? `${EU_COUNTRIES[c]} (${c})` : c;
};

/* ============================================================
 * Defensive accessors
 * ============================================================ */

const pickViesData = (rd) => {
  if (!rd || typeof rd !== "object") return {};
  // OpenAPI.it shape reale: { data: [ {vatCode, companyName, valid, formatValid, countryCode, companyAddress} ] }
  // Vecchie shape legacy: { data: {…} } oppure campi al top level.
  let d;
  if (Array.isArray(rd.data) && rd.data.length > 0) d = rd.data[0] || {};
  else if (rd.data && typeof rd.data === "object") d = rd.data;
  else d = rd;
  return {
    valid: d.valid === true || d.isValid === true || d.is_valid === true,
    countryCode: d.countryCode || d.country_code || d.country || d.paese,
    vatNumber: d.vatCode || d.vatNumber || d.vat_number || d.vat || d.piva,
    companyName: d.companyName || d.company_name || d.name || d.denominazione,
    address: d.companyAddress || d.address || d.indirizzo,
    requestDate: d.requestDate || d.request_date || d.date,
    requestId: d.requestId || d.request_id || d.id,
  };
};

/* ============================================================
 * ResultVies
 * ============================================================ */

export const ResultVies = ({ request, onNewSearch, onRetry }) => {
  const navigate = useNavigate();
  const completedAt = request?.completed_at || request?.created_at;
  const v = pickViesData(request?.response_data);
  const country = v.countryCode ? String(v.countryCode).toUpperCase() : null;
  const vatNum = v.vatNumber ? String(v.vatNumber) : (request?.params?.piva || request?.params?.vat || "");
  const vatLabel = country && vatNum ? `${country} ${vatNum}` : (vatNum || EM_DASH);

  const onExportJson = () => {
    try {
      const blob = new Blob([JSON.stringify(request, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vies_${request?.id || "result"}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore export JSON"); }
  };

  const onPrint = () => window.print();
  const handleNewSearch = () => (onNewSearch ? onNewSearch() : navigate("/search/vies"));
  const handleRetry = () => (onRetry ? onRetry() : navigate("/search/vies"));

  /* --------------------- VALID --------------------- */
  if (v.valid) {
    return (
      <div className="max-w-3xl" data-testid="result-vies-valid">
        <BackLink to="/history" label="Storico richieste" />

        <HeroStatus
          variant="success"
          title="Partita IVA valida"
          description={`Verificata sul sistema VIES dell'Unione Europea · ${fmtItalianDateTime(completedAt)}`}
        />

        <Section>
          <EntityCard
            title="Soggetto identificato"
            entity={String(v.companyName || EM_DASH).toUpperCase()}
            meta={vatLabel}
          />
        </Section>

        <Section>
          <Card>
            <CardTitle>Dati anagrafici</CardTitle>
            <KvGrid columns={2}>
              <KvItem label="Paese" value={countryLabel(country)} testId="kv-country" />
              <KvItem
                label="Stato VIES"
                value="Attiva e registrata"
                valueColor="success"
                testId="kv-state"
              />
              <KvItem
                label="Indirizzo sede legale"
                value={orDash(v.address)}
                columnSpan
                testId="kv-address"
              />
              <KvItem label="Data verifica" value={fmtItalianDateTime(completedAt)} testId="kv-date" />
              <KvItem label="ID richiesta" value={v.requestId || request?.id || EM_DASH} mono testId="kv-reqid" />
            </KvGrid>
          </Card>
        </Section>

        <FooterActions>
          <ActionButton variant="primary" icon={Plus} onClick={handleNewSearch} testId="action-new">
            Nuova ricerca
          </ActionButton>
          <ActionButton variant="outline" icon={Download} onClick={onExportJson} testId="action-export-json">
            Esporta JSON
          </ActionButton>
          <ActionButton variant="outline" icon={Printer} onClick={onPrint} testId="action-print">
            Stampa
          </ActionButton>
        </FooterActions>

        <TechDetailsToggle data={request} />
      </div>
    );
  }

  /* --------------------- INVALID --------------------- */
  return (
    <div className="max-w-3xl" data-testid="result-vies-invalid">
      <BackLink to="/history" label="Storico richieste" />

      <HeroStatus
        variant="danger"
        title="Partita IVA non valida"
        description={`Il numero non risulta registrato al sistema VIES · ${fmtItalianDateTime(completedAt)}`}
      />

      <Section>
        <Card>
          <CardTitle>Dettagli verifica</CardTitle>
          <KvGrid columns={2}>
            <KvItem label="P.IVA verificata" value={vatLabel} mono testId="kv-piva" />
            <KvItem label="Paese" value={countryLabel(country)} testId="kv-country" />
            <KvItem
              label="Stato VIES"
              value="Non registrata"
              valueColor="danger"
              testId="kv-state"
            />
            <KvItem label="Data verifica" value={fmtItalianDateTime(completedAt)} testId="kv-date" />
          </KvGrid>

          <Divider />

          <Prose>
            Il numero inserito non risulta attivo nel registro VIES dell'Unione Europea.
            Verifica che la partita IVA sia stata digitata correttamente e che includa il
            prefisso paese (es. <strong className="text-text-primary">IT</strong>{vatNum || "12345678901"}).
          </Prose>
        </Card>
      </Section>

      <FooterActions>
        <ActionButton variant="primary" icon={RotateCw} onClick={handleRetry} testId="action-retry">
          Riprova
        </ActionButton>
        <ActionButton variant="outline" icon={Plus} onClick={handleNewSearch} testId="action-new">
          Nuova ricerca
        </ActionButton>
      </FooterActions>

      <TechDetailsToggle data={request} />
    </div>
  );
};
